/* eslint-disable */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import ScoreGauge from "@/components/ScoreGauge";
import PillarBar from "@/components/PillarBar";
import JurisdictionGrid from "@/components/JurisdictionGrid";
import Disclaimer from "@/components/Disclaimer";
import ScoreExplanationModal from "@/components/ScoreExplanationModal";
import StateAlertCTA from "@/components/StateAlertCTA";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { ArrowRight, ExternalLink, AlertCircle, AlertTriangle, Info, CheckCircle2, FileDown } from "lucide-react";

// ---------- TAB 1 — Growing Store (Maple & Co.) ----------
const GROWING = {
  store_name: "Maple & Co. Home Goods",
  store_url: "demo-store.myshopify.com",
  revenue_tier: "$500k-$2M",
  composite: 67,
  grade: "C",
  band: "Moderate Readiness — Review Suggested",
  pillar1: 58, pillar2: 65, pillar3: 82, pillar4: 75,
  lastVerified: "2026-08-18",
  nextReview: "2026-08-25",
  jurisdictionAnalysis: {
    FL: { status: "compliant", summary: "FL sales tax nexus triggered. Merchant registered with FL DOR. FDBR exempt (revenue below $1B).",
      verifiedSourceProof: { authorityName: "Florida Dept of Revenue", officialURL: "https://floridarevenue.com", statuteCitation: "Fla. Stat. § 212.0596", lastVerifiedDate: "2026-08-18", nextReviewDate: "2026-08-25" } },
    TX: { status: "action_required", summary: "TX nexus triggered but NOT registered with Texas Comptroller.",
      verifiedSourceProof: { authorityName: "Texas Comptroller", officialURL: "https://comptroller.texas.gov", statuteCitation: "Tex. Tax Code § 151.107", lastVerifiedDate: "2026-08-18", nextReviewDate: "2026-08-25" } },
    NY: { status: "not_applicable", summary: "Revenue met ($540K) but transactions (87) below 100. Dual-threshold not met.",
      verifiedSourceProof: { authorityName: "NY Dept of Tax", officialURL: "https://www.tax.ny.gov", statuteCitation: "N.Y. Tax Law § 1101(b)(8)(iv)", lastVerifiedDate: "2026-08-18", nextReviewDate: "2026-08-25" } },
    DC: { status: "compliant", summary: "DC nexus triggered. Merchant registered with DC OTR.",
      verifiedSourceProof: { authorityName: "DC OTR", officialURL: "https://otr.cfo.dc.gov", statuteCitation: "D.C. Code § 47-2201", lastVerifiedDate: "2026-08-18", nextReviewDate: "2026-08-25" } },
  },
  remediation: [
    { priority: "CRITICAL", jurisdiction: "TX", action: "Register for sales tax collection in Texas", potentialGain: 22, statuteCitation: "Tex. Tax Code § 151.107", url: "https://comptroller.texas.gov" },
    { priority: "HIGH", jurisdiction: "TX", action: "Add opt-out mechanism for TDPSA compliance", potentialGain: 12, statuteCitation: "Tex. Bus. & Com. Code § 541", url: "https://www.texasattorneygeneral.gov" },
    { priority: "MEDIUM", jurisdiction: "ALL", action: "Add cookie consent banner", potentialGain: 8, statuteCitation: "Best practice (TX, FL)", url: "" },
    { priority: "MEDIUM", jurisdiction: "ALL", action: "Add privacy contact to policy", potentialGain: 6, statuteCitation: "TX TDPSA infrastructure", url: "" },
    { priority: "LOW", jurisdiction: "ALL", action: "Add data retention disclosure to policy", potentialGain: 4, statuteCitation: "TX TDPSA best-practice", url: "" },
  ],
  header: null,
};

// ---------- TAB 2 — Established Store (Harborview Home Goods) ----------
const ESTABLISHED = {
  store_name: "Harborview Home Goods",
  store_url: "harborview-demo.myshopify.com",
  revenue_tier: "$2M+",
  composite: 91,
  grade: "A",
  band: "Strong Readiness",
  pillar1: 100, pillar2: 88, pillar3: 100, pillar4: 100,
  lastVerified: "2026-08-18",
  nextReview: "2026-08-25",
  header: "This store has strong readiness. Even high-scoring stores benefit from ongoing monitoring as thresholds drift and new state laws take effect.",
  jurisdictionRows: [
    { abbr: "CA", status: "compliant", summary: "CA nexus registered. CCPA/CPRA consumer policies in place. All three CCPA criteria assessed." },
    { abbr: "TX", status: "compliant", summary: "TX nexus registered with Texas Comptroller. TDPSA opt-out mechanism present on website." },
    { abbr: "NY", status: "compliant", summary: "Both $500K revenue and 100+ transactions met. Registered with NY Dept of Tax and Finance." },
    { abbr: "FL", status: "compliant", summary: "FL nexus registered with FL DOR. FDBR exempt — revenue below $1 billion threshold." },
    { abbr: "CO", status: "review_suggested", summary: "CO nexus registered. CPA opt-out present. Consider explicitly honoring Global Privacy Control (GPC) browser signal." },
    { abbr: "WA", status: "review_suggested", summary: "WA nexus registered. Review whether any health-adjacent products fall within MHMDA scope under RCW § 70.372." },
  ],
  remediation: [
    { priority: "LOW", jurisdiction: "Colorado", action: "Consider explicitly honoring the Global Privacy Control (GPC) browser signal for Colorado CPA compliance.", potentialGain: 5, statuteCitation: "C.R.S. § 6-1-1308", url: "https://coag.gov" },
    { priority: "LOW", jurisdiction: "Washington", action: "Review whether any health-adjacent products trigger MHMDA scope.", potentialGain: 4, statuteCitation: "RCW § 70.372", url: "https://www.atg.wa.gov" },
  ],
};

const PRIORITY_META = {
  CRITICAL: { color: "var(--rg-red)", Icon: AlertCircle },
  HIGH: { color: "var(--rg-amber)", Icon: AlertTriangle },
  MEDIUM: { color: "var(--rg-sage)", Icon: Info },
  LOW: { color: "var(--rg-muted)", Icon: Info },
};

const STATUS_BADGE = {
  compliant: { label: "COMPLIANT", color: "var(--rg-sage)", Icon: CheckCircle2 },
  review_suggested: { label: "REVIEW SUGGESTED", color: "var(--rg-amber)", Icon: AlertTriangle },
  action_required: { label: "ACTION REQUIRED", color: "var(--rg-red)", Icon: AlertCircle },
  not_applicable: { label: "N/A", color: "var(--rg-muted)", Icon: Info },
};

const SCENARIOS = { growing: GROWING, established: ESTABLISHED };

export default function SampleReport() {
  const [scenarioKey, setScenarioKey] = useState("growing");
  // 2026-08-18 — pull the most recent bulk-verify date from the live DB so
  // the "Regulatory data last verified" banner never drifts behind the
  // admin's actual verification cadence. Falls back to the hardcoded
  // date on each scenario if the fetch fails (e.g. backend down during
  // marketing browsing) — the fallback matches what the page rendered
  // before this fix was applied. See backend/routes/public_routes.py::
  // public_verification_status.
  const [liveVerification, setLiveVerification] = useState(null);
  useEffect(() => {
    api.get("/public/verification-status")
      .then(({ data }) => {
        if (data && data.last_verified_date) setLiveVerification(data);
      })
      .catch(() => { /* silent — hardcoded fallback covers it */ });
  }, []);
  const D = SCENARIOS[scenarioKey];

  const exportPdf = () => {
    document.body.classList.add("printing");
    const suggested = `Regulens-Readiness-${D.store_url.replace(/[^a-z0-9.-]/gi, "-")}-${new Date().toISOString().slice(0, 10)}`;
    const prevTitle = document.title;
    document.title = suggested;
    window.print();
    setTimeout(() => {
      document.body.classList.remove("printing");
      document.title = prevTitle;
    }, 1500);
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-12">
        {/* Scenario tabs */}
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="sample-scenario-tabs">
          <Button
            size="sm"
            onClick={() => setScenarioKey("growing")}
            className={scenarioKey === "growing" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"}
            data-testid="sample-tab-growing"
          >
            Growing Store — Score 67/C
          </Button>
          <Button
            size="sm"
            onClick={() => setScenarioKey("established")}
            className={scenarioKey === "established" ? "bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" : "bg-[var(--rg-surface)] border border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface-2)]"}
            data-testid="sample-tab-established"
          >
            Established Store — Score 91/A
          </Button>
        </div>

        {/* Illustrative banner */}
        <div
          className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-4 mb-4 text-sm text-[var(--rg-amber)]"
          data-testid="sample-illustration-banner"
        >
          This is a sample report for a fictional store. All scores and findings are illustrative examples only.
        </div>

        {/* Per-scenario header banner */}
        {D.header && (
          <div className="rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-4 mb-4 text-sm text-[var(--rg-text)]" data-testid="sample-scenario-header">
            <CheckCircle2 className="w-4 h-4 inline mr-2 text-[var(--rg-sage)]" />
            {D.header}
          </div>
        )}

        {/* PDF Export button — public page, no tier restriction */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-1">Sample Report · Educational</div>
            <h1 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl text-[var(--rg-text-strong)]">Sample Compliance Report — {D.store_name}</h1>
            <div className="text-sm text-[var(--rg-muted)] font-mono mt-1">{D.store_url} · Revenue tier {D.revenue_tier}</div>
          </div>
          <Button onClick={exportPdf} variant="outline" className="border-[var(--rg-border)] text-[var(--rg-text-strong)] hover:bg-[var(--rg-surface)]" data-testid="sample-export-pdf">
            <FileDown className="w-4 h-4 mr-2" /> ↓ Export Readiness Report as PDF
          </Button>
        </div>

        <p className="text-[var(--rg-text)] mb-6 max-w-3xl">
          This is a realistic example of what your Regulens report looks like. Scores and findings are based on a fictional store profile.
        </p>

        <div className="rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-5 mb-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="sample-overall-banner">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)]">Overall Score</div>
            <div className="font-heading font-extrabold text-4xl text-[var(--rg-text-strong)] mt-1">{D.composite}<span className="text-[var(--rg-muted)] text-2xl font-mono">/100</span></div>
            <div className="text-sm text-[var(--rg-text)] mt-1 font-mono">Grade {D.grade} · {D.band}</div>
          </div>
          <Link to="/register">
            <Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold">Audit My Real Store <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div className="rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 flex flex-col items-center justify-center">
            <ScoreGauge score={D.composite} testId="sample-gauge" />
            <div className="mt-2 text-xs uppercase tracking-[0.18em] font-bold text-[var(--rg-text)]" data-testid="sample-grade">Grade {D.grade}</div>
            <ScoreExplanationModal />
          </div>
          <div className="lg:col-span-2 rounded-lg border border-[var(--rg-border)] bg-[var(--rg-surface)] p-6 space-y-5">
            <PillarBar label="Sales Tax Nexus" score={D.pillar1} weight="35%" testId="sample-pillar-1" />
            <PillarBar label="Privacy Law Readiness" score={D.pillar2} weight="30%" testId="sample-pillar-2" />
            <PillarBar label="Policy Documents" score={D.pillar3} weight="20%" testId="sample-pillar-3" />
            <PillarBar label="Exposure Awareness" score={D.pillar4} weight="15%" testId="sample-pillar-4" />
          </div>
        </div>

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-4">Per-jurisdiction analysis</h2>
        {scenarioKey === "growing" ? (
          <JurisdictionGrid analysis={D.jurisdictionAnalysis} testIdPrefix="sample-juris" />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="sample-jurisdiction-grid-est">
            {D.jurisdictionRows.map((row) => {
              const meta = STATUS_BADGE[row.status] || STATUS_BADGE.not_applicable;
              return (
                <div key={row.abbr} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-4" data-testid={`sample-juris-est-${row.abbr}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading font-extrabold text-[var(--rg-text-strong)] text-2xl">{row.abbr}</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1A` }}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--rg-text)] leading-relaxed">{row.summary}</p>
                </div>
              );
            })}
          </div>
        )}

        <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mt-10 mb-4">Remediation plan</h2>
        <div className="space-y-3">
          {D.remediation.map((r, i) => {
            const meta = PRIORITY_META[r.priority];
            return (
              <div key={`${r.priority}-${r.action}`} className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 flex flex-col sm:flex-row gap-3 sm:items-start" data-testid={`sample-remediation-${i}`}>
                <div className="flex items-center gap-2 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wide self-start" style={{ color: meta.color, borderColor: meta.color, borderWidth: 1, borderStyle: "solid", backgroundColor: `${meta.color}1a` }}>
                  <meta.Icon className="w-3.5 h-3.5" /> {r.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[var(--rg-text-strong)] font-semibold break-words">{r.action}</div>
                  <div className="text-xs text-[var(--rg-muted)] font-mono mt-1 break-all">{r.jurisdiction} · {r.statuteCitation} {r.url && (<a href={r.url} target="_blank" rel="noreferrer" className="text-[var(--rg-sage)] ml-1 inline-flex items-center gap-1">source<ExternalLink className="w-3 h-3"/></a>)}</div>
                </div>
                <div className="text-[var(--rg-sage)] font-mono font-bold text-lg self-start">+{r.potentialGain}</div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5 text-xs text-[var(--rg-muted)] font-mono" data-testid="sample-data-freshness">
          Regulatory data last verified: <span className="text-[var(--rg-text-strong)]" data-testid="sample-last-verified">{liveVerification?.last_verified_date || D.lastVerified}</span> · Next scheduled review: <span className="text-[var(--rg-text-strong)]">{liveVerification?.next_review_date || D.nextReview}</span>
        </div>

        <div className="mt-12 rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] p-8 text-center">
          <h3 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-2">See your own compliance score</h3>
          <Link to="/register">
            <Button size="lg" className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-bold">Audit My Real Store <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </Link>
        </div>

        {/* 2026-08-18 — Sticky state-alert email capture. Isolated component,
            does not touch nav, existing CTAs, or scoring. Backend at
            backend/routes/public_routes.py::state_alert_subscribe. */}
        <StateAlertCTA />

        <div className="mt-6"><Disclaimer /></div>
      </section>
    </Layout>
  );
}
